import { useCallback, useEffect, useState } from 'react'

const EVENT_PREFIX = 'local-storage:'

export function useLocalStorage<T>(key: string, initial: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key)
      if (raw == null) return initial
      return JSON.parse(raw) as T
    } catch {
      return initial
    }
  })

  useEffect(() => {
    const eventName = EVENT_PREFIX + key
    function onLocal(e: Event) {
      const detail = (e as CustomEvent<T>).detail
      setValue(detail)
    }
    function onStorage(e: StorageEvent) {
      if (e.key !== key) return
      try {
        setValue(e.newValue == null ? initial : (JSON.parse(e.newValue) as T))
      } catch {
        // ignore malformed value
      }
    }
    window.addEventListener(eventName, onLocal)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(eventName, onLocal)
      window.removeEventListener('storage', onStorage)
    }
  }, [key, initial])

  const set = useCallback(
    (next: T) => {
      setValue(next)
      try {
        localStorage.setItem(key, JSON.stringify(next))
      } catch {
        // localStorage may be unavailable (private mode, quota); fall through silently.
      }
      window.dispatchEvent(new CustomEvent(EVENT_PREFIX + key, { detail: next }))
    },
    [key],
  )

  return [value, set]
}
